import { Box } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IESignRuleService } from "../../../../../../models/esign-rule";
import MainApi from "../../../../../../services/mainApi.service";
import { InteroperabilityTab } from "../BasicData/InteroperabilityTab";

const translationPath = "4uESign.page.automations.automationRules.";

const _4UESIGN_RULE_SERVICE_ROUTE =
  process.env.REACT_APP_4UESIGN + "/api/v1/ESignRuleService/";

const service = new MainApi<IESignRuleService>(_4UESIGN_RULE_SERVICE_ROUTE);

export const InteroperabilityPage = memo(() => {
  const { id: ruleId } = useParams();
  const { t } = useTranslation();

  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>();

  const columns: GridColDef<IESignRuleService>[] = [
    {
      field: "service",
      headerName: t(translationPath + "service") || "Service",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "direction",
      headerName: t(translationPath + "direction") || "Direction",
      width: 200,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
  ];

  const handleAdd = () => {
    setSelectedId(undefined);
    setShowForm(true);
  };

  const handleRowClick = (params: GridRowParams<IESignRuleService>) => {
    if (!params.row) return;
    setSelectedId(params.row.id);
    setShowForm(true);
  };

  const handleBack = () => {
    setSelectedId(undefined);
    setShowForm(false);
  };

  const params = useMemo(() => {
    return {
      RuleId: ruleId,
    };
  }, [ruleId]);

  if (!ruleId) return null;

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {showForm ? (
        <Box
          sx={(theme) => ({
            p: 1,
            height: "100%",
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
            borderRadius: 1,
          })}
        >
          <InteroperabilityTab
            id={selectedId}
            ruleId={ruleId}
            onBack={handleBack}
          />
        </Box>
      ) : (
        <Box height={600}>
          <Table<IESignRuleService>
            showDefaultMacroFilters={false}
            service={service}
            columns={columns}
            title={
              t(translationPath + "interoperability") ||
              "Interoperability Services"
            }
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            serviceProps={params}
            defaultPageSize={20}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={
              t(translationPath + "interroperability_search") ||
              "Search Service..."
            }
            addButtonLabel={t(translationPath + "add_service") || "Add Service"}
            confirmOptions={{
              title:
                t(translationPath + "interroperability_delete.title") ||
                "Confirm Deletion",
              description:
                t(translationPath + "interroperability_delete.description") ||
                "Are you sure you want to delete this service?",
              confirmationButtonProps: {
                variant: "contained",
                color: "error",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
});
